import { useMemo } from 'react';

import { Skeleton } from '@components/index';
import { useQueryMovieCreditsTMDB, useQueryMovieTMDB } from '@hooks/index';
import { ISchedule } from '@interfaces/scheduleInterface';
import { IconPhotoOff } from '@tabler/icons-react';
import { durationMovie } from '@utils/durationMovie';
import dayjs from 'dayjs';
import truncate from 'just-truncate';
import { ChevronDown, ChevronUp } from 'react-feather';

import { ICast, ICrew } from '../../interfaces/movieInterface';

type ScheduleCastProps = {
	open: boolean;
	setScheduleOpen: (scheduleId: number | undefined) => void;
	schedule: ISchedule;
};

const ScheduleCast = ({ open, setScheduleOpen, schedule }: ScheduleCastProps) => {
	const { data: credits, isFetching: isLoadingCredits } = useQueryMovieCreditsTMDB(
		Number(schedule.movie.movieDBId) as number,
		{ enabled: open }
	);

	const { data: movie, isFetching: isLoadingMovie } = useQueryMovieTMDB({
		movieTMDBId: Number(schedule.movie.movieDBId) as number,
		config: { enabled: open }
	});

	const handlerOnOpen = (movieTMDBId: number | undefined) => {
		setScheduleOpen(movieTMDBId ? schedule.id : undefined);
	};

	const staff = useMemo<(ICrew | ICast)[]>(() => {
		if (!credits) return [];
		const director = credits.crew.filter((crew) => crew.job === 'Director')[0];
		return [director, ...credits.cast.slice(0, 6)];
	}, [credits]);

	return (
		<>
			{schedule.movie.movieDBId && open && (
				<div className="my-4 flex justify-center md:my-2 md:justify-start">
					<button
						type="button"
						className="text-md flex cursor-pointer items-center justify-center gap-1 text-slate-500"
						onClick={() => handlerOnOpen(undefined)}
					>
						<ChevronUp size={18} />
						<div>Fermer les informations du film</div>
					</button>
				</div>
			)}
			{schedule.movie.movieDBId && !open && (
				<div className="my-4 flex justify-center md:my-2 md:justify-start">
					<button
						type="button"
						className="text-md flex cursor-pointer items-center justify-center gap-1 text-slate-500"
						onClick={() => handlerOnOpen(schedule.movie.movieDBId as number)}
					>
						<ChevronDown size={18} />
						<div>Voir les informations du film</div>
					</button>
				</div>
			)}

			{open && (isLoadingMovie || movie) && (
				<div className="mt-4 flex flex-col gap-6 md:flex-row">
					<div className="md:w-col-30 lg:w-col-20 mx-auto flex justify-center md:mx-0">
						{isLoadingMovie && (
							<div className="aspect-[2/3] w-40 animate-pulse rounded-lg bg-slate-200 md:w-full" />
						)}

						{!isLoadingMovie && movie && (
							<>
								{movie.poster_path && (
									<img
										src={`https://image.tmdb.org/t/p/w342${movie.poster_path}`}
										alt={movie.title}
										className="aspect-[2/3] w-40 rounded-lg object-cover md:w-full"
									/>
								)}
								{!movie.poster_path && (
									<div className="flex aspect-[2/3] w-40 items-center justify-center rounded-lg bg-slate-200 px-2 text-center text-xl md:w-full">
										Image non disponible
									</div>
								)}
							</>
						)}
					</div>
					<div className="md:w-col-70 lg:w-col-80 flex flex-col gap-4 text-center md:text-left">
						<div className="flex flex-col gap-1">
							{isLoadingMovie && <Skeleton height={28} width={250} />}
							{!isLoadingMovie && movie && (
								<h3 className="text-lg font-bold">
									{movie.title} ({dayjs(movie.release_date).format('YYYY')})
								</h3>
							)}

							{isLoadingMovie && <Skeleton height={24} width={150} />}
							{!isLoadingMovie && movie && (
								<h2 className="text-base">
									<span className="font-bold">Durée:</span> {durationMovie(movie.runtime)}
								</h2>
							)}
						</div>

						<div className="text-gray-700">
							{isLoadingMovie && (
								<div className="flex flex-col gap-0.5">
									<Skeleton height={19} width="98%" />
									<Skeleton height={19} width="98%" />
									<Skeleton height={19} width="98%" />
									<Skeleton height={19} width="60%" />
								</div>
							)}

							{!isLoadingMovie && movie && <>{truncate(movie.overview, 500)}</>}
						</div>

						<div>
							{isLoadingCredits && (
								<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-3 md:gap-6 lg:grid-cols-4 xl:grid-cols-7">
									{Array.from({ length: 7 }).map((_, i) => (
										<div key={i} className="flex flex-col items-center gap-2">
											<div className="size-24 animate-pulse rounded-full bg-slate-200 sm:size-28 md:size-32 lg:size-36" />
											<Skeleton height={16} width={80} />
											<Skeleton height={14} width={56} />
										</div>
									))}
								</div>
							)}

							{!isLoadingCredits && staff && (
								<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-3 md:gap-6 lg:grid-cols-4 xl:grid-cols-7">
									{staff.map((cast) => (
										<div key={cast.id} className="flex flex-col items-center gap-2">
											<div className="flex size-24 items-center justify-center overflow-hidden rounded-full bg-slate-200 sm:size-28 md:size-32 lg:size-36">
												{cast.profile_path ? (
													<img
														src={`https://image.tmdb.org/t/p/w185/${cast.profile_path}`}
														alt={cast.name}
														className="size-full object-cover"
													/>
												) : (
													<IconPhotoOff size={40} className="text-slate-400" />
												)}
											</div>
											<div className="flex h-16 flex-col items-center justify-center text-center">
												<span className="line-clamp-2 text-base leading-tight font-medium">
													{cast.name}
												</span>
												<span className="truncate text-sm text-gray-500">
													{(cast as ICrew).job === 'Director' ? 'Réalisateur' : 'Acteur'}
												</span>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default ScheduleCast;
